/**
 * One entry, turned into the words a person would search for.
 *
 * **Why a document at all.** `values_json` is the wrong haystack. Its keys are
 * field uuids and most of its values are uuids too: a sale holds the customer's
 * id, not his name. Searching it for "Ramesh" finds nothing. So a document is
 * RENDERED from the entry, once per write, and the index holds that.
 *
 * **The one rule the whole design rests on.** A document is a pure function of
 * the entry's OWN values and its OWN record's schema. Nothing else. No
 * reference's title, no record name, no other entry.
 *
 * That rule is what makes the index maintainable. Put "Ramesh Kumar" into every
 * sale that names him and renaming him once makes every one of those documents
 * wrong — and finding them again needs a reverse index nobody wants to keep.
 * With his id instead, a rename invalidates exactly ONE document, his own, and
 * every sale keeps pointing at him correctly. The cost is one extra query at
 * search time, which `search-query.ts` builds.
 *
 * **Three columns, because bm25 takes a weight per column.**
 *
 *   title   the entry's own name, when it has one
 *   body    its own other words and digits
 *   refs    the ids of the entries it points at
 *
 * A name then outranks a note word, and a hit reached through a reference sits
 * between the two.
 *
 * **This function must never throw.** It runs inside the transaction that saves
 * an entry. A throw here would fail the save, so a shop would lose a sale over
 * a search index. Every branch is total, unknown shapes render nothing, and a
 * test asserts it against hostile input.
 */
import type { EntryValue, EntryValues } from './api';

import { tOr } from './i18n/t';


/* ------------------------------------------------------- the three helpers */

/**
 * The field a binding names, or null when the record binds none.
 *
 * A copy of the app's own `fieldFor`, and it lives here because a document is
 * rendered on BOTH sides now: the phone writes one into FTS5 as it saves, and
 * the server writes the same one into `data.entry_search`. Two renderers would
 * mean a word the phone finds and the server does not.
 */
const fieldFor = (schema: SearchableRecord, purpose: string): string | null => {
  const binding = schema.bindings[purpose];
  return binding !== undefined && 'fieldId' in binding ? binding.fieldId : null;
};

/**
 * The place a location value NAMES, or null when it is only coordinates.
 *
 * `lat,lng;Name` is the stored shape. A pair of numbers is not a word anybody
 * searches for, so it contributes nothing.
 */
const placeNameOf = (stored: string): string | null => {
  const [pair, ...rest] = stored.split(';');
  const name = rest.join(';').trim();
  if (name !== '') return name;
  const parts = (pair ?? '').split(',');
  const isPoint =
    parts.length === 2 && parts.every((part) => part.trim() !== '' && Number.isFinite(Number(part)));
  if (isPoint) return null;
  const typed = stored.trim();
  return typed === '' ? null : typed;
};

/**
 * The unit word a quantity carries: "5 kg" should find the row holding 5 and
 * kilograms.
 *
 * Two shapes, which are the two a unit can take. A `unit_list` of exactly one
 * is a record that measures one thing. A `unit_field` is a sibling the entry
 * chose, and only a select or a text field can be one — so the word is that
 * option's label or that text, and nothing here formats a number or a date.
 */
const unitWordOf = (
  schema: SearchableRecord,
  field: SearchableField,
  values: EntryValues,
): string | null => {
  const named = field.props['unit_slot_field'];
  if (typeof named === 'string') {
    const sibling = schema.fields.find((candidate) => candidate.id === named);
    const held = values[named];
    if (sibling !== undefined && typeof held === 'string') {
      if (sibling.type === 'select') {
        const option = (sibling.options ?? []).find((candidate) => candidate.id === held);
        const word = option === undefined ? '' : (option.label ?? tOr(option.labelKey, ''));
        return word === '' ? null : word;
      }
      return held.trim() === '' ? null : held;
    }
  }
  const list = field.props['unit_list'];
  if (Array.isArray(list) && list.length === 1 && typeof list[0] === 'string') return list[0];
  return null;
};


/**
 * What a document needs to know about a field, and no more.
 *
 * A structural subset of `RecordSchemaField`, so a full schema is assignable to
 * it and every existing call site is unchanged. It exists because the SERVER
 * renders documents too, inside the transaction that saves an entry, and
 * assembling a whole `RecordSchemaResponse` there would mean reading six tables
 * on the write path for five properties.
 */
export type SearchableField = {
  readonly id: string;
  readonly type: string;
  readonly storage: string;
  readonly props: Record<string, unknown>;
  readonly options?: readonly { readonly id: string; readonly labelKey: string; readonly label: string | null }[] | null;
};

/** The same, for the record: its fields and which binding names its title. */
export type SearchableRecord = {
  readonly bindings: Readonly<Record<string, { readonly fieldId: string } | { readonly constNum: 1 | -1 } | undefined>>;
  readonly fields: readonly SearchableField[];
};

export type SearchDocument = {
  /** The entry's own name, or '' when the name belongs to another entry. */
  readonly title: string;
  /** Every other word and digit the entry itself holds. */
  readonly body: string;
  /** The ids of the entries this one points at, dashes stripped. */
  readonly refs: string;
};

export const EMPTY_DOCUMENT: SearchDocument = { title: '', body: '', refs: '' };

/**
 * The two value types that name ANOTHER entry.
 *
 * A `reference` names one and a `multi_select` names several — and a
 * multi_select's value is a list of entry uuids, not option ids: the server
 * refuses anything else (`apps/server/src/entry-values.ts`, "a list of uuids"),
 * and reads them back from `data.entry_link` in `ord` order. So both land in
 * `refs`, and neither contributes a word.
 */
export const POINTS_AT_AN_ENTRY: ReadonlySet<string> = new Set(['reference', 'multi_select']);

/**
 * An id as ONE token.
 *
 * `unicode61` treats a hyphen as a separator, so a uuid would otherwise become
 * five short tokens — and `refs:("a1b2")` would then match any id sharing that
 * first group. Stripping the dashes makes each id a single, unambiguous term.
 */
export const refToken = (id: string): string => id.replace(/-/g, '');

/** Only the digits. What a shop actually types when it means a number. */
const digitsOf = (text: string): string => text.replace(/[^0-9]/g, '');

/**
 * The words ONE field contributes, or '' when it contributes none.
 *
 * A reference is absent from this on purpose: it contributes an id, not a word,
 * and `refsOf` collects those.
 *
 * Exported because the SEARCH SCREEN needs it too. A result row says which
 * field it matched in, and the only honest way to work that out is to ask the
 * same function that put the words in the index. See `search-present.ts`.
 */
export const fieldWords = (
  schema: SearchableRecord,
  field: SearchableField,
  values: EntryValues,
): string => {
  // A computed field is never on the device. The server refuses to write one
  // into an entry and skips it again on read, so there is nothing to render.
  if (field.storage === 'computed') return '';

  const value: EntryValue | undefined = values[field.id];
  if (value === null || value === undefined || value === '') return '';

  switch (field.type) {
    case 'text':
    case 'long_text':
      return typeof value === 'string' ? value : '';

    case 'phone': {
      if (typeof value !== 'string') return '';
      const digits = digitsOf(value);
      if (digits === '') return '';
      // TWO tokens, and this is a real gap that had to be closed. A phone
      // that names its country is stored as `+` and digits — the form saves
      // E.164 and the server takes the spaces out of any other `+` number —
      // so "+919876543210" is one long token and a search for "98765" would
      // miss it: a prefix matches only from the START of a term. The last ten
      // digits are what a shop types, so they go in as a term of their own.
      const national = digits.length > 10 ? digits.slice(-10) : '';
      return national === '' ? digits : `${digits} ${national}`;
    }

    case 'number':
    case 'money':
      // The RAW digits, never `formatMoney`. That draws ₹1,023, and the comma
      // splits it into the two terms "1" and "023" — so a shop typing 1023
      // would find nothing. `search-query.ts` takes the commas out of what is
      // typed, so the two meet in the middle.
      return typeof value === 'number' ? String(value) : '';

    case 'quantity': {
      if (typeof value !== 'number') return '';
      // The unit is a word a shop searches by: "5 kg" should find the row that
      // holds 5 and kilograms. unitOf needs no reference names for the two
      // shapes it serves — a list of one, and a sibling field the entry chose.
      const unit = unitWordOf(schema, field, values);
      return unit === null ? String(value) : `${String(value)} ${unit}`;
    }

    case 'select': {
      if (typeof value !== 'string') return '';
      const option = (field.options ?? []).find((candidate) => candidate.id === value);
      if (option === undefined) return '';
      // `label ?? t(labelKey)`, the rule api.ts states on the field: a choice
      // somebody typed carries its own word, a template's carries a key. tOr
      // and not t, because this must not throw inside a write.
      return option.label ?? tOr(option.labelKey, '');
    }

    case 'location':
      // The place a location NAMES. A pair of coordinates is not a word.
      return typeof value === 'string' ? (placeNameOf(value) ?? '') : '';

    // Left out on purpose, each for its own reason.
    //
    //   date, datetime  "aug" would match a month of entries and "2026" a
    //                   year of them. The date filter answers this exactly,
    //                   and phase 3 reads a date out of the search box.
    //   boolean         "yes" would match half of every record holding a tick.
    //   attachment      an opaque file id. No filename is ever stored.
    //   reference       an id, not a word. refsOf collects it.
    //   multi_select    a list of ids. Same.
    default:
      return '';
  }
};

/**
 * Which field gives this entry its own name.
 *
 * **This is deliberately NOT `titleOf`.** That function resolves a
 * reference-bound title through names the caller hands it, because a list must
 * draw "IPhone" on the row. A document holds only the entry's own words, so a
 * reference-bound title has no name here at all: the word "IPhone" belongs to
 * the Items row and is reached through `refs`.
 *
 * If it fell through instead, as `titleOf` does, a sale's NOTE would become its
 * title and be weighted four times — which inverts the reason the weight
 * exists. A note is not a name.
 *
 * The fallthrough is kept for a record with NO title binding, where the first
 * filled text field genuinely is the row's own name.
 *
 * Exported for the same reason as `fieldWords`: a result row must know whether
 * its match landed in the title, because that is when it draws no field label.
 */
export const ownNameField = (
  schema: SearchableRecord,
  values: EntryValues,
): SearchableField | null => {
  const bound = fieldFor(schema, 'title');
  if (bound !== null) {
    const field = schema.fields.find((candidate) => candidate.id === bound);
    if (field === undefined) return null;
    if (POINTS_AT_AN_ENTRY.has(field.type)) return null;
    return fieldWords(schema, field, values) === '' ? null : field;
  }
  for (const field of schema.fields) {
    if (field.type !== 'text' && field.type !== 'long_text') continue;
    if (fieldWords(schema, field, values) !== '') return field;
  }
  return null;
};

/** Every entry id this entry points at, as single tokens. */
const refsOf = (schema: SearchableRecord, values: EntryValues): string[] => {
  const ids: string[] = [];
  for (const field of schema.fields) {
    if (!POINTS_AT_AN_ENTRY.has(field.type)) continue;
    const value = values[field.id];
    if (typeof value === 'string' && value !== '') {
      ids.push(refToken(value));
      continue;
    }
    if (Array.isArray(value)) {
      for (const one of value) {
        if (typeof one === 'string' && one !== '') ids.push(refToken(one));
      }
    }
  }
  return ids;
};

/** Collapse runs of whitespace, so a document holds no empty terms. */
const tidy = (text: string): string => text.replace(/\s+/g, ' ').trim();

/**
 * The document for one entry.
 *
 * Total by construction: an unknown field type, a value of the wrong shape and
 * a schema missing a field all render nothing rather than throwing.
 */
export const renderSearchDoc = (
  schema: SearchableRecord,
  values: EntryValues,
): SearchDocument => {
  const nameField = ownNameField(schema, values);
  const parts: string[] = [];

  for (const field of schema.fields) {
    // The name is not repeated in the body. Doing so would give it both
    // weights and quietly double the rank of every title hit.
    if (nameField !== null && field.id === nameField.id) continue;
    const words = fieldWords(schema, field, values);
    if (words !== '') parts.push(words);
  }

  return {
    title: nameField === null ? '' : tidy(fieldWords(schema, nameField, values)),
    body: tidy(parts.join(' ')),
    refs: refsOf(schema, values).join(' '),
  };
};

/* ------------------------------------------------------------- the lines */

/**
 * The document of one LINE, which is never stored as a document of its own.
 *
 * docs/plans/entry-lines-and-layout.md, Decision 19: search never returns a
 * line. A guest name finds the stay, so the words of every line, at both
 * levels, are folded into the document of the entry at the top of its tree
 * (`foldLineDocs`), and no line holds a row in the index.
 *
 * A line is rendered by the rule an entry is, with one difference: its
 * `parent` and `root` are left out. They are the server's own references up
 * the tree, and a ref to the stay inside the stay's own document would only
 * make every stay point at itself. `serverSet` names them when the schema in
 * hand binds neither (the server's write context keeps them apart from its
 * bindings); a schema that binds them is read for them as well.
 */
export const renderLineDoc = (
  schema: SearchableRecord,
  values: EntryValues,
  serverSet: ReadonlySet<string> = new Set(),
): SearchDocument => {
  const skip = new Set(serverSet);
  for (const purpose of ['parent', 'root']) {
    const bound = fieldFor(schema, purpose);
    if (bound !== null) skip.add(bound);
  }
  return renderSearchDoc(
    { bindings: schema.bindings, fields: schema.fields.filter((field) => !skip.has(field.id)) },
    values,
  );
};

/**
 * An entry's own document with the words of its lines folded in.
 *
 * The entry keeps its title: a line's name is not the entry's name, so it goes
 * into the body with the rest of the line's words, where a guest name still
 * finds the stay but ranks below a stay named for it. The lines' refs join the
 * entry's, so an item a sale line points at finds the sale the way a reference
 * on the sale itself would. Each ref once.
 *
 * Total, like `renderSearchDoc`: no lines answers the entry's own document.
 */
export const foldLineDocs = (doc: SearchDocument, lines: readonly SearchDocument[]): SearchDocument => {
  if (lines.length === 0) return doc;
  const words = [doc.body];
  const refs: string[] = [];
  const seen = new Set<string>();
  const addRefs = (tokens: string): void => {
    for (const token of tokens.split(' ')) {
      if (token === '' || seen.has(token)) continue;
      seen.add(token);
      refs.push(token);
    }
  };
  addRefs(doc.refs);
  for (const line of lines) {
    words.push(line.title, line.body);
    addRefs(line.refs);
  }
  return { title: doc.title, body: tidy(words.join(' ')), refs: refs.join(' ') };
};
